import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, Lightning } from '@phosphor-icons/react';

const plans = [
  { key: 'monthly', name: 'Monthly', price: 49, period: '/mo', features: ['Live signals', 'Full analysis', 'Live chat', 'Telegram + WhatsApp'] },
  { key: 'quarterly', name: 'Quarterly', price: 129, period: '/3mo', features: ['Everything in Monthly', 'Priority support', '12% savings'] },
  { key: 'yearly', name: 'Yearly', price: 399, period: '/yr', features: ['Everything in Quarterly', 'Private community', '32% savings'], popular: true },
  { key: 'lifetime', name: 'Lifetime', price: 999, period: 'once', features: ['Everything, forever', 'VIP status', 'Founding member perks'] },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [subs, setSubs] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get('/subscriptions/me'); setSubs(data); })(); }, []);

  const subscribe = async (plan) => {
    try {
      const { data } = await api.post('/subscribe', { plan });
      toast.success(`Subscribed to ${plan}! Premium activated.`);
      await refresh();
      window.location.href = data.checkout_url || '/dashboard';
    } catch (err) { toast.error(err.response?.data?.detail || 'Subscription failed'); }
  };

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <div className="data-label text-saffron">Subscription</div>
        <h1 className="font-heading text-3xl font-bold mt-1">Upgrade your terminal</h1>
        <p className="text-muted-foreground mt-2">Choose a plan that fits your trading style. MOCKED Stripe checkout — will redirect to real Stripe when API key is configured in Admin → Integrations.</p>
      </div>

      {user?.is_premium && (
        <div className="glass p-4 rounded-lg neon-border border-green-500/40 flex items-center gap-3">
          <Lightning size={24} className="text-green-400" weight="fill" />
          <div>
            <div className="font-heading font-bold">Premium Active</div>
            <div className="text-xs text-muted-foreground">Expires: {user.premium_expires_at ? new Date(user.premium_expires_at).toLocaleDateString() : '—'}</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map(p => (
          <div key={p.key} className={`glass p-6 rounded-xl relative ${p.popular ? 'neon-border border-saffron/60 shadow-glow-saffron' : 'neon-border'}`}>
            {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 gradient-orange text-white text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1 rounded-full">Popular</div>}
            <div className="data-label">{p.name}</div>
            <div className="mt-3 mb-6"><span className="font-mono text-4xl font-black">${p.price}</span><span className="text-muted-foreground ml-1">{p.period}</span></div>
            <ul className="space-y-2 mb-6 text-sm">
              {p.features.map(f => <li key={f} className="flex items-start gap-2"><Check size={14} className="text-saffron mt-0.5" weight="bold" />{f}</li>)}
            </ul>
            <Button onClick={() => subscribe(p.key)} data-testid={`subscribe-${p.key}`} className={`w-full ${p.popular ? 'bg-saffron hover:bg-saffron-hover text-white' : 'bg-white/5 hover:bg-white/10 text-white'}`}>Subscribe</Button>
          </div>
        ))}
      </div>

      {subs.length > 0 && (
        <div className="glass p-6 rounded-lg">
          <h3 className="font-heading font-bold text-lg mb-3">Your Subscriptions</h3>
          <div className="space-y-2">
            {subs.map(s => (
              <div key={s.id} className="flex justify-between text-sm p-3 bg-surface-base rounded">
                <span className="capitalize font-semibold">{s.plan}</span>
                <span className="font-mono">${s.price}</span>
                <span className="text-muted-foreground">Expires {new Date(s.expires_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
